import { useState, MouseEvent, Fragment, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { LinkType } from "types/menu";
import { useSetRecoilState, useRecoilState } from "recoil";
import { sidebarState } from "base/store/sidebar";
import { routeState } from "base/store/route";
import {
  MANUSCRIPT_BOOK_URL,
  MANUSCRIPT_NOVEL_URL,
  MANUSCRIPT_ALL_URL,
  MANUSCRIPT_LIST_URL,
} from "manuscript/constants/route";
import { BaseManuscriptPageParams } from "types/page";

interface Props {
  links: LinkType[] | undefined;
  level: number;
}

const MenuList = ({ links, level }: Props) => {
  const { category_type, category_id } = useParams<BaseManuscriptPageParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const setSidebarData = useSetRecoilState(sidebarState);
  const [routeStateData, setRouteState] = useRecoilState(routeState);
  const [expandMap, setExpandMap] = useState<{
    [key: string | number]: boolean;
  }>({});

  useEffect(() => {
    if (!links) {
      return;
    }

    // only process manuscript pages, so this comment is only for manuscript pages
    // [0]: empty
    // [1]: always manuscript
    // [2]: page type (list | view)
    // [3]: category type (novel | book | all)
    // [4]: category id
    const pathnameParams = location.pathname.split("/");
    links.forEach((link, idx) => {
      if (link.defaultExpand) {
        setExpandMap((prev) => ({ ...prev, [idx]: true }));
      }

      if (
        (link.href === MANUSCRIPT_BOOK_URL && pathnameParams[3] === "book") ||
        (link.href === MANUSCRIPT_NOVEL_URL && pathnameParams[3] === "novel")
      ) {
        setExpandMap((prev) => ({ ...prev, [idx]: true }));
      }

      // Init data for page title
      if (location.pathname === link.href) {
        setRouteState({
          currentRouteName: link.text,
          currentSidebarHref: link.href,
        });
      }
    });
  }, [links]);

  const renderExpandIcon = (
    isExpand: boolean | undefined,
    index: string | number
  ) => {
    if (!isExpand) return null;

    const onClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setExpandMap({ ...expandMap, [index]: !expandMap[index] });
    };

    return (
      <ListItemIcon onClick={onClick}>
        {expandMap[index] ? <ExpandLess /> : <ExpandMore />}
      </ListItemIcon>
    );
  };

  const checkSelectedItem = (href: string) => {
    if (location.pathname === href) {
      return true;
    }

    if (`${MANUSCRIPT_LIST_URL}/${category_type}/${category_id}` === href) {
      return true;
    }

    if (
      href === MANUSCRIPT_ALL_URL &&
      location.pathname.indexOf("all") !== -1
    ) {
      return true;
    }

    return false;
  };

  if (!links) {
    return <></>;
  }

  return (
    <List disablePadding>
      {links.map(
        ({ href, text, icon: Icon, cantNavigate, isExpand, subLinks }, idx) => {
          return (
            <Fragment key={href}>
              <ListItem disablePadding>
                <ListItemButton
                  sx={{ pl: level === 0 ? 2 : level * 4 }}
                  selected={checkSelectedItem(href)}
                  onClick={() => {
                    if (!cantNavigate) {
                      setRouteState({
                        ...routeStateData,
                        currentRouteName: text,
                        currentSidebarHref: href,
                      });
                      navigate(href);
                      setSidebarData({ isShowSidebar: false });
                    }

                    if (!expandMap[idx]) {
                      setExpandMap({ ...expandMap, [idx]: true });
                    }
                  }}
                >
                  <ListItemIcon>{Icon && <Icon />}</ListItemIcon>
                  <ListItemText primary={text} />
                  {renderExpandIcon(isExpand, idx)}
                </ListItemButton>
              </ListItem>

              {subLinks && (
                <Collapse in={expandMap[idx]} timeout="auto">
                  <MenuList links={subLinks} level={level + 1} />
                </Collapse>
              )}
            </Fragment>
          );
        }
      )}
    </List>
  );
};

export default MenuList;
